import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Divider,
  useMediaQuery,
  Theme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import LogoutIcon from "@mui/icons-material/Logout";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";

// Theme definition for consistent styling
const theme = {
  colors: {
    primary: "#FF7043", // Orange
    primaryDark: "#F4511E",
    secondary: "#FFE0B2", // Light Orange
    background: "#FFF3E0", // Very Light Orange
    text: "#37474F", // Dark Gray
    white: "#FFFFFF",
    gray: "#9E9E9E",
  },
  spacing: {
    xs: "0.5rem",
    sm: "1rem",
    md: "1.5rem",
    lg: "2rem",
    xl: "3rem",
  },
  borderRadius: {
    sm: "8px",
    md: "12px",
    lg: "20px",
    circle: "50%",
  },
  shadows: {
    small: "0 2px 8px rgba(255, 112, 67, 0.1)",
    medium: "0 4px 20px rgba(255, 112, 67, 0.15)",
    large: "0 10px 40px rgba(255, 112, 67, 0.2)",
  },
};

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    transition: "all 0.3s ease",
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.colors.primary,
      },
    },
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.colors.primary,
        borderWidth: "2px",
      },
    },
  },
  "& .MuiFormLabel-root.Mui-focused": {
    color: theme.colors.primary,
  },
}));

const LogoutButton = styled(Button)(({ isMobile }: { isMobile?: boolean }) => ({
  backgroundColor: theme.colors.white,
  color: theme.colors.text,
  padding: isMobile ? "8px 12px" : "12px 24px",
  borderRadius: theme.borderRadius.md,
  boxShadow: theme.shadows.small,
  textTransform: "none",
  fontSize: isMobile ? "0.8rem" : "1rem",
  fontWeight: 500,
  border: `1px solid ${theme.colors.gray}`,
  minWidth: isMobile ? "auto" : "120px",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "#f5f5f5",
    borderColor: theme.colors.text,
  },
}));

const UserSection = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing.md,
  marginBottom: theme.spacing.lg,
  padding: theme.spacing.md,
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.md,
  boxShadow: theme.shadows.small,
}));

const CoachingInfoBox = styled(Box)(() => ({
  padding: theme.spacing.md,
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.md,
  boxShadow: theme.shadows.small,
  marginTop: theme.spacing.lg,
}));

const Settings: React.FC = () => {
  const user = auth.currentUser;
  // Check if the device is mobile
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        maxWidth: "600px",
        margin: "0 auto",
        overflow: "auto",
        height: "100%",
      }}
    >
      <Typography
        variant="h6"
        sx={{ mb: 3, color: theme.colors.text, fontWeight: 600 }}
      >
        Settings
      </Typography>

      <UserSection>
        <Avatar
          src={user?.photoURL || undefined}
          alt={user?.displayName || "User"}
          sx={{ width: 48, height: 48 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.displayName}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.email}
          </Typography>
        </Box>
        <LogoutButton
          onClick={handleLogout}
          startIcon={isMobile ? null : <LogoutIcon />}
          isMobile={isMobile}
        >
          {isMobile ? <LogoutIcon fontSize="small" /> : "Sign Out"}
        </LogoutButton>
      </UserSection>

      <Divider sx={{ my: 3 }} />

      <CoachingInfoBox>
        <Typography variant="h6" sx={{ mb: 2 }}>
          About Your AI Coach
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This AI is designed to be your non-directive coach, helping you
          discover your own insights and solutions through supportive
          conversation.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your coach will listen empathetically, ask open-ended questions,
          facilitate reflection, and empower you to identify your own strategies
          - all without giving direct advice.
        </Typography>
      </CoachingInfoBox>
    </Box>
  );
};

export default Settings;
